import React, { useCallback, useEffect, useRef, useState } from 'react';
import useLiveRefresh from '../utils/useLiveRefresh';
import { APP_VERSION } from '../utils/appVersion';
import {
    Link,
    useNavigate,
    useLocation
} from 'react-router-dom';
import {getCurrentUserRole, getCurrentUserOrgId, getCurrentUserId} from '../utils/authHelper';
import {logout, isLoggedIn} from '../auth';
import {getAllOrganisations} from '../services/OrganisationService';
import {useOrg} from '../context/OrgContext';
import {getPendingHolidayCount} from '../services/HolidayService';
import {getPendingFlexDayCount} from '../services/FlexDayService';
import {getPendingOvertimeCount} from '../services/OvertimeApprovalService';
import {getFollowUps} from '../services/AcquisitionService';
import OpenTasksModal from '../modals/OpenTasksModal';
import api from '../services/api';
import '../styles/NavigationBar.css';
import '../styles/OpenTasksModal.css';

// The 1100px rung of the breakpoint ladder, kept in sync with NavigationBar.css by hand: a media
// query condition cannot read a custom property.
const MENU_BREAKPOINT = 1100;

// A group is a landing link plus its submenu. On desktop the submenu opens on hover; in the panel
// the chevron button opens it, because a tap on the trigger navigates instead of opening.
function NavGroup({ id, to, label, isActive, isOpen, onToggle, children }) {
    const submenuId = `nav-submenu-${id}`;
    return (
        <div className={`nav-dropdown ${isActive ? 'active' : ''} ${isOpen ? 'open' : ''}`}>
            <Link to={to} className="nav-dropdown-trigger">
                {label}
            </Link>
            <button
                type="button"
                className="nav-dropdown-toggle"
                aria-expanded={isOpen}
                aria-controls={submenuId}
                aria-label={`Untermenü ${label}`}
                onClick={() => onToggle(id)}
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M6 9l6 6 6-6"/>
                </svg>
            </button>
            <div className="nav-dropdown-menu" id={submenuId}>
                {children}
            </div>
        </div>
    );
}

export default function NavigationBar() {
    const navigate = useNavigate();
    const location = useLocation();
    const role = getCurrentUserRole();
    const { activeOrgId, setActiveOrg } = useOrg();
    const [orgs, setOrgs] = useState([]);
    const [openDaysCount, setOpenDaysCount] = useState(0);
    const [pendingHolidaysCount, setPendingHolidaysCount] = useState(0);
    const [holidayStatusBadge, setHolidayStatusBadge] = useState(0);
    const [isTasksOpen, setIsTasksOpen] = useState(false);
    const [logoUrl, setLogoUrl] = useState('/logo.png');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [openGroup, setOpenGroup] = useState(null);
    const navRef = useRef(null);

    useEffect(() => {
        if (role === 'super_admin') {
            getAllOrganisations()
                .then(data => setOrgs(data))
                .catch(() => {});
        }
    }, [role]);

    useEffect(() => {
        const orgId = activeOrgId || getCurrentUserOrgId();
        if (!orgId) return;
        api.get(`/Organisation/${orgId}/logo`, { responseType: 'blob', skipSessionRedirect: true })
            .then(res => setLogoUrl(URL.createObjectURL(res.data)))
            .catch(() => setLogoUrl('/logo.png'));
    }, [activeOrgId]);

    // Polled, not mount-only: the open-days hint used to need a page reload to appear.
    useLiveRefresh(() => {
        api.get('/Notification/open-days')
            .then(res => setOpenDaysCount(res.data?.count ?? 0))
            .catch(() => setOpenDaysCount(0));
    }, { enabled: isLoggedIn() });

    useLiveRefresh(() => {
        Promise.all([
            getPendingHolidayCount(),
            getPendingFlexDayCount(),
            getPendingOvertimeCount(),
            // Calls that are due today belong on the same daily badge as approvals.
            getFollowUps({ scope: 'mine' }).then(r => r.count ?? 0).catch(() => 0),
        ])
            .then(([h, g, o, c]) => setPendingHolidaysCount(h + g + o + c))
            .catch(() => {});
    }, { enabled: (role === 'admin' || role === 'super_admin') && isLoggedIn() });

    useLiveRefresh(() => {
        const uid = getCurrentUserId();
        if (!uid) return;
        api.get(`/Holiday/user/${uid}`)
            .then(res => {
                const stored = JSON.parse(localStorage.getItem('holidayStatusMap') || '{}');
                const count = (res.data || []).filter(h => {
                    const s = h.approval_status ?? 'pending';
                    return s !== 'pending' && stored[String(h.id)] !== s;
                }).length;
                setHolidayStatusBadge(count);
            })
            .catch(() => {});
    }, { enabled: role === 'user' && isLoggedIn() });

    const burgerRef = useRef(null);

    const closeMenu = useCallback(() => {
        // Return focus to the control that opened the panel; dropping it on <body>
        // leaves a keyboard user at the top of the document.
        setIsMenuOpen((wasOpen) => {
            if (wasOpen) burgerRef.current?.focus();
            return false;
        });
        setOpenGroup(null);
    }, []);

    // Keyed on the path, not the location: a tap on a link to the current route is closed by
    // handleMenuClick instead.
    useEffect(() => {
        closeMenu();
    }, [location.pathname, closeMenu]);

    useEffect(() => {
        if (!isMenuOpen) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeMenu();
        };
        const onPointerDown = (e) => {
            if (!navRef.current?.contains(e.target)) closeMenu();
        };
        // Past the breakpoint the burger is gone, so an open panel would strand its scroll lock.
        const onResize = () => {
            if (window.innerWidth > MENU_BREAKPOINT) closeMenu();
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onPointerDown);
        window.addEventListener('resize', onResize);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onPointerDown);
            window.removeEventListener('resize', onResize);
        };
    }, [isMenuOpen, closeMenu]);

    useEffect(() => {
        if (!isMenuOpen) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        // Cleanup runs on close and on unmount — a leaked lock freezes every page behind the panel.
        return () => {
            document.body.style.overflow = previous;
        };
    }, [isMenuOpen]);

    const toggleMenu = () => {
        if (isMenuOpen) closeMenu();
        else setIsMenuOpen(true);
    };

    // One group at a time keeps the panel short enough to reach its last entry.
    const toggleGroup = useCallback((id) => {
        setOpenGroup(current => (current === id ? null : id));
    }, []);

    const handleMenuClick = (e) => {
        // A tap on the chevron expands a group; a tap on a link is a navigation and closes the panel.
        if (e.target.closest('a')) closeMenu();
    };

    const handleLogout = () => {
        setActiveOrg(null, null);
        logout();
        navigate('/');
    };

    const handleOrgChange = (e) => {
        const id = e.target.value || null;
        const name = id ? orgs.find(o => o.id === id)?.name ?? null : null;
        setActiveOrg(id, name);
        // Reload the current page so data re-fetches with the new org scope
        navigate(0);
    };

    const isActive = (path) => {
        if (path === '/employee') return location.pathname === '/employee';
        return location.pathname.startsWith(path);
    };

    return (
        <>
        <nav className="navbar" ref={navRef}>
            <div className="navbar-left">
                <img src={logoUrl} alt="Logo" className="logo"/>

                <button
                    type="button"
                    className="nav-burger"
                    ref={burgerRef}
                    aria-expanded={isMenuOpen}
                    aria-controls="nav-menu"
                    aria-label={isMenuOpen ? 'Navigation schließen' : 'Navigation öffnen'}
                    onClick={toggleMenu}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        {isMenuOpen ? (
                            <>
                                <path d="M18 6L6 18"/>
                                <path d="M6 6l12 12"/>
                            </>
                        ) : (
                            <>
                                <path d="M3 6h18"/>
                                <path d="M3 12h18"/>
                                <path d="M3 18h18"/>
                            </>
                        )}
                    </svg>
                </button>

                {/* Without this the dismissing tap also activates whatever control sits
                    under the finger. It lives inside .navbar so navRef.contains still holds. */}
                {isMenuOpen && (
                    <div className="nav-backdrop" onClick={closeMenu} aria-hidden="true" />
                )}

                <div
                    id="nav-menu"
                    className={`nav-menu ${isMenuOpen ? 'nav-menu-open' : ''}`}
                    onClick={handleMenuClick}
                >
                {(role === 'admin' || role === 'super_admin') && (
                    <>
                        <NavGroup
                            id="customers"
                            to="/customers"
                            label="Kunden"
                            isActive={isActive('/customers')}
                            isOpen={openGroup === 'customers'}
                            onToggle={toggleGroup}
                        >
                            <Link to="/customer_contacts" className="nav-dropdown-item">
                                Kontakte
                            </Link>
                        </NavGroup>

                        <NavGroup
                            id="projects"
                            to="/projects"
                            label="Projekte"
                            isActive={isActive('/projects') || isActive('/project-tickets')
                                || isActive('/planning') || isActive('/move-allocations')}
                            isOpen={openGroup === 'projects'}
                            onToggle={toggleGroup}
                        >
                            <Link to="/project-tickets" className="nav-dropdown-item">
                                Tickets
                            </Link>
                            <Link to="/planning" className="nav-dropdown-item">
                                Planung
                            </Link>
                        </NavGroup>

                        <NavGroup
                            id="sales"
                            to="/project_acquisition"
                            label="Vertrieb"
                            isActive={isActive('/project_acquisition') || isActive('/products') || isActive('/leads')}
                            isOpen={openGroup === 'sales'}
                            onToggle={toggleGroup}
                        >
                            <Link to="/leads" className="nav-dropdown-item">
                                Leadliste
                            </Link>
                            <Link to="/products" className="nav-dropdown-item">
                                Produkte
                            </Link>
                        </NavGroup>

                        <Link to="/invoices" className={isActive('/invoices') ? 'active' : ''}>
                            Finanzen
                        </Link>

                    </>
                )}

                {(role === 'user' || role === 'admin') && (
                    <Link to="/kpi" className={isActive('/kpi') ? 'active' : ''}>Dashboard</Link>
                )}

                {(role === 'user' || role === 'admin') && (
                    <div className="nav-item-with-badge">
                        <Link to="/timetracking" className={isActive('/timetracking') ? 'active' : ''}>
                            Zeiterfassung
                        </Link>
                        {openDaysCount > 0 && (
                            <Link
                                to="/timetracking?filter=open"
                                className="nav-badge"
                                title={`${openDaysCount} offene Arbeitstage`}
                            >
                                {openDaysCount}
                            </Link>
                        )}
                    </div>
                )}
                {(role === 'user' || role === 'admin') && (
                    <div className="nav-item-with-badge">
                        <Link to="/employee" className={isActive('/employee') ? 'active' : ''}>
                            Abwesenheiten
                        </Link>
                        {holidayStatusBadge > 0 && <span className="nav-badge">{holidayStatusBadge}</span>}
                    </div>
                )}
                {role === 'user' && isLoggedIn() && (
                    <Link to={`/employee-hours/${getCurrentUserId()}`} className={isActive('/employee-hours') ? 'active' : ''}>
                        Stundenkonto
                    </Link>
                )}
                {(role === 'admin' || role === 'super_admin') && (
                    <NavGroup
                        id="employees"
                        to="/employee-hours"
                        label="Mitarbeiter"
                        isActive={isActive('/employee-hours') || isActive('/employee_profile')}
                        isOpen={openGroup === 'employees'}
                        onToggle={toggleGroup}
                    >
                        <Link to="/employee_profile" className="nav-dropdown-item">
                            Profile
                        </Link>
                    </NavGroup>
                )}
                {role === 'super_admin' && (
                    <Link to="/admin" className={isActive('/admin') ? 'active' : ''}>Admin Panel</Link>
                )}

                <Link to="/settings" className={isActive('/settings') ? 'active' : ''}>Einstellungen</Link>
                </div>
            </div>
            <div className="navbar-right">
                {APP_VERSION && (
                    <span className="nav-version" title={`Scalewisp ${APP_VERSION}`}>
                        {APP_VERSION}
                    </span>
                )}
                {role === 'super_admin' && orgs.length > 0 && (
                    <div className="org-selector">
                        <select
                            value={activeOrgId || ''}
                            onChange={handleOrgChange}
                            className="org-select"
                            title="Organisation auswählen"
                        >
                            <option value="">Alle Organisationen</option>
                            {orgs.map(o => (
                                <option key={o.id} value={o.id}>{o.name}</option>
                            ))}
                        </select>
                    </div>
                )}
                {(role === 'admin' || role === 'super_admin') && isLoggedIn() && (
                    <div className="nav-tasks-btn">
                        <button
                            className="nav-tasks-icon"
                            onClick={() => setIsTasksOpen(true)}
                            aria-label="Offene Aufgaben"
                            title="Offene Aufgaben"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                            </svg>
                            {pendingHolidaysCount > 0 && (
                                <span className="nav-badge">{pendingHolidaysCount}</span>
                            )}
                        </button>
                    </div>
                )}
                {isLoggedIn() && (
                    <button data-testid="nav-logout" onClick={handleLogout} className="button button-danger">
                        Logout
                    </button>
                )}
            </div>
        </nav>
        <OpenTasksModal
            isOpen={isTasksOpen}
            onClose={() => setIsTasksOpen(false)}
            onCountChange={setPendingHolidaysCount}
        />
        </>
    );
}
